/* eslint-disable no-nested-ternary */
import {
  Box, Skeleton, ThemeProvider, useTheme,
} from '@mui/material';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { ContentMembership } from '@optimus/models';
import ContentHeroBanner from './ContentHeroBanner';
import ContentCardArticle from './ContentCardArticle';
import ContentCardHeader from './ContentCardHeader';
import ContentFooterView from './ContentFooterView';
import { useAppDispatch } from '../../../store/hooks';
import * as contentAPI from '../../../api/contentAPI';
import { ContentContactSalesDialog } from './ContentContactSalesDialog';
import { useLockedContentAction } from './useLockedContentAction';
import {
  ContentFeedSection,
  ContentLikeStatus,
  ContentPodcast,
  MetaItem,
} from '../../../common/models/content/Content';
import { getContentFormAnswers } from '../../../store/thunks/contentThunk';
import { Question } from '../../../common/models/Question';
import { ContentTheme } from './ContentTheme';
import PodcastListenSubscribeSheet from './PodcastListenSubscribeSheet';
import ContentFormView, { ContentFormQuestionProps } from './ContentFormView';
import { TestIds } from '../../../assets/testIds';

interface ContentCardProps {
  categoryText?: string | null,
  banner?: {
    url: string | undefined;
    width: number | undefined;
    height: number | undefined;
  } | null,
  logo?: string | null,
  contentText: string,
  contentShortDescription: string,
  authorTitle?: string | null,
  publishedDate?: string,
  contentHeroListener?: () => void,
  videoUrl?: string | null
  contentId: number
  contentLikeStatus?: ContentLikeStatus
  formId: string | null
  metadata?: MetaItem[]
  podcast?: ContentPodcast
  isHorizontalCard?: boolean
  contentFeedSection?: ContentFeedSection
  contentFeedSectionPosition?: number
  locked?: boolean
  lowestMembershipRequired?: ContentMembership
}

export default function ContentCard(props: ContentCardProps) {
  const [ContentHeroComponent, setContentHeroComponent] = useState<React.FC | null>(null);
  const [PollContentHeader, setPollContentHeader] = useState<React.FC | null>(null);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isPoll = useMemo(() => (
    props.categoryText?.toUpperCase() === 'POLL'
  ), [props.categoryText]);

  const {
    requiresSalesContact,
    hasContactedSales,
    handleLockedClick,
    contactSalesDialogProps,
  } = useLockedContentAction({
    contentId: props.contentId,
    contentTitle: props.contentText,
    contentCreatorName: props.authorTitle,
    type: props.categoryText,
    metadata: props.metadata,
    lowestMembershipRequired: props.lowestMembershipRequired,
  });

  // A locked card is clickable in full - image and content area - whatever its content type, and
  // takes the same action as the badge on it. Unlocked cards keep their existing behaviour:
  // articles open the content, other types are not card-clickable.
  const handleCardClick: ((e: React.SyntheticEvent) => void) | undefined = props.locked ?
    handleLockedClick :
    (props.categoryText?.toUpperCase() === 'ARTICLE' ? props.contentHeroListener : undefined);

  /**
   * The hero component is built once in an effect with no dependencies, so anything it
   * closes over is frozen at first render. The locked badge has to react to the contact
   * sales request completing, so it reads through this ref, which is refreshed on every
   * render and read while the stored component renders (SN-996).
   */
  const lockedActionRef = useRef({ handleLockedClick, requiresSalesContact, hasContactedSales });
  lockedActionRef.current = { handleLockedClick, requiresSalesContact, hasContactedSales };

  useEffect(() => {
    if (isPoll && props.formId) {
      const getContentFormResponse = contentAPI.getContentForm(props.formId);
      const getContentFormAnswersResponse =
        dispatch(getContentFormAnswers(props.formId));
      Promise.all([getContentFormResponse, getContentFormAnswersResponse])
        .then(([contentFormResponse]) => {
          let formQuestion: ContentFormQuestionProps;
          if (contentFormResponse?.questions && contentFormResponse?.questions?.length > 0) {
            // considering poll will have only one question
            const questionItem: Question = contentFormResponse?.questions[0];
            formQuestion = {
              questionId: questionItem.id,
              questionType: questionItem.type,
              title: questionItem.title,
              totalVotes: questionItem.totalVotes,
              options: questionItem.options ? questionItem.options : [],
            };
          }
          setContentHeroComponent(() => (
            () => <ContentFormView
              formId={contentFormResponse?.id}
              question={formQuestion}
              contentId={props.contentId}
              contentCreatorName={props.authorTitle}
              contentMetaData={props.metadata}
              title={props.categoryText}
              heading={props.contentText}
            />
          ));
          setPollContentHeader(() => (
            () => <ContentCardHeader
              metadata={props.metadata}
              categoryText={props.categoryText}
              contentText={formQuestion?.title || ''}
              contentShortDescription={props.contentShortDescription}
            />
          ));
        });
    } else if (props.categoryText?.toUpperCase() === 'ARTICLE' ||
      props.categoryText?.toUpperCase() === 'PODCAST') {
      setContentHeroComponent(() => (
        () => <ContentCardArticle
          OnclickListener={props.contentHeroListener}
          banner={props.banner}
          locked={props.locked}
          onUpgradeClick={(e) => lockedActionRef.current.handleLockedClick(e)}
          requiresSalesContact={lockedActionRef.current.requiresSalesContact}
          hasContactedSales={lockedActionRef.current.hasContactedSales}
        />
      ));
    } else {
      setContentHeroComponent(() => (
        () => <ContentHeroBanner
          // Locked content must not open: the click falls through to the card's upgrade handler.
          OnclickListener={props.locked ? undefined : props.contentHeroListener}
          banner={props.banner}
          title={props.categoryText}
          heading={props.contentText}
          shortDescription={props.contentShortDescription}
          embedVideoUrl={props.videoUrl}
          contentId={props.contentId}
          contentCreatorName={props.authorTitle}
          logo={props.logo}
          contentMetaData={props.metadata}
          contentFeedSection={props.contentFeedSection}
          contentFeedSectionPosition={props.contentFeedSectionPosition}
        />
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={ContentTheme}>
      <Box
        sx={{
          display: 'flex',
          gap: theme.spacing(2),
          [theme.breakpoints.down('md')]: {
            // 300px to ensure youtube player has enough space
            minWidth: theme.spacing(37.5),
          },
          ...(!props.isHorizontalCard && {
            [theme.breakpoints.down('sm')]: {
              width: 'auto',
            },
            flexDirection: 'column',
            flex: '1 0 0',
          }),
          ...(isPoll && {
            borderRadius: theme.spacing(2),
            p: theme.spacing(2),
            backgroundColor: theme.palette.grey[100],
          }),
          cursor: handleCardClick ? 'pointer' : 'default',
        }}
        data-testid={TestIds.contentFeed.contentCard}
        onClick={handleCardClick}
        // Keyboard users get the same click target as mouse users when the card is clickable.
        {...(handleCardClick && {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleCardClick(event);
            }
          },
        })}
      >
        {isPoll && PollContentHeader && <PollContentHeader/>}
        {ContentHeroComponent ? <Box
          sx={{
            [theme.breakpoints.down('sm')]: {
              maxWidth: '100%',
            },
            ...(props.isHorizontalCard && {
              width: '100%',
              maxWidth: theme.spacing(50),
            }),
          }}
        >
          <ContentHeroComponent />
        </Box> : <Skeleton
          sx={() => ({
            borderRadius: theme.spacing(1.25),
            [theme.breakpoints.down('sm')]: {
              height: 180,
            },
            height: 250,
          })}
          animation="wave" variant="rectangular" />}
        {!isPoll &&
          <Box
            id='content-header-footer-container'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing(2),
              alignSelf: 'stretch',
              ...(props.isHorizontalCard && {
                width: '100%',
              }),
            }}
          >
            {/* header */}
            <ContentCardHeader
              metadata={props.metadata}
              categoryText={props.categoryText}
              contentText={props.contentText}
              contentShortDescription={props.contentShortDescription}
              lowestMembershipRequired={props.lowestMembershipRequired}
            />
            {/* end header */}

            {/* only for podcast */}
            {props.categoryText?.toUpperCase() === 'PODCAST' &&
              <Box
                sx={{
                  width: '100%',
                  alignSelf: 'stretch',
                  ...(props.isHorizontalCard && {
                    width: 'auto',
                    maxWidth: theme.spacing(40),
                  }),
                }}
              >
                <PodcastListenSubscribeSheet
                  contentId={props.contentId}
                  contentCreatorName={props.authorTitle}
                  contentTitle={props.contentText}
                  podcast={props.podcast}
                  title={props.categoryText}
                  heading={props.contentText}
                  contentMetaData={props.metadata}
                  contentFeedSection={props.contentFeedSection}
                  contentFeedSectionPosition={props.contentFeedSectionPosition}
                />
              </Box>
            }

            {/* footer */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
              }}
            >
              <ContentFooterView
                contentId={props.contentId}
                contentLikeStatus={props.contentLikeStatus}
                contentTitle={props.contentText}
                contentCreatorName={props.authorTitle}
                logo={props.logo}
                // Locked content must not open: ContentFooterView invokes this for non-ARTICLE
                // cards, so the click has to fall through to the card's upgrade handler instead.
                OnclickListener={props.locked ? undefined : props.contentHeroListener}
                contentMetadata={props.metadata}
                contentType={props.categoryText || ''}
                contentFeedSection={props.contentFeedSection}
                contentFeedSectionPosition={props.contentFeedSectionPosition}
                locked={props.locked}
                lowestMembershipRequired={props.lowestMembershipRequired}
              />
            </Box>
            {/* end footer */}
          </Box >
        }
      </Box >
      <ContentContactSalesDialog {...contactSalesDialogProps} />
    </ThemeProvider>);
}
